import type {Dispatch, FunctionComponent, SetStateAction, ReactElement} from 'react'
import {useRef, useState} from 'react'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'

import {useInView} from 'react-intersection-observer'
import clsx from 'clsx'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {BrgIconArrowBack} from '~/icons/brg-icon-arrow-back'
import {BrgIconArrowForward} from '~/icons/brg-icon-arrow-forward'
import type {InViewRefCardProps, Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'

import {getDomainConfig} from '~/domains'
import {DwsRelatedProductCard} from './related-product-card'

const CARDS_GAP_PIXELS = 16

export const RelatedProducts: FunctionComponent = () => {
  const {language, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const {
    dataResponse: {relatedProducts},
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const [refItems, setRefItems] = useState<InViewRefCardProps>({
    firstItemInView: true,
    lastItemInView: false,
  })

  const numberOfDwsItemsInList = relatedProducts.reduce(
    (count: number, product: Product) => count + (product.isSellerProduct ? 1 : 0),
    0,
  )

  const discount = Math.max(
    ...(relatedProducts || []).map((product: Product) => Math.max(product.coupon?.value || 0, product?.discount || 0)),
  )

  const reportArrowClick = useReportWidgetClick()

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'related-products',
    widget_place: 'middle',
    count_products: relatedProducts.length,
    count_dws_items: numberOfDwsItemsInList,
  })

  const carouselRef = useRef<HTMLDivElement>(null)
  const firstCardElementRef = useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'back' | 'forward') => {
    const scrollLeft = carouselRef.current?.scrollLeft ?? 0
    const cardWidth = firstCardElementRef.current?.getBoundingClientRect().width ?? 0

    if (cardWidth) {
      carouselRef.current?.scroll({
        left:
          direction === 'back' ? scrollLeft - cardWidth - CARDS_GAP_PIXELS : cardWidth + CARDS_GAP_PIXELS + scrollLeft,
        behavior: 'smooth',
      })
      reportArrowClick({
        widget_name: direction === 'back' ? 'arrow-left-scroll' : 'arrow-right-scroll',
        widget_place: 'related-products',
      })
    }
  }

  return (
    relatedProducts &&
    !!relatedProducts.length && (
      <section
        ref={widgetRef}
        data-testid="related-products"
        id="related-products"
        className={clsx(
          'relative flex w-full flex-col rounded-3xl pb-8 pl-6 pr-0 pt-7',
          stylesBydomain.LISTPAGE_WIDGETS_RELATED_PRODUCTS_BG,
          'listpage_widgets_related_products_bg',
        )}
      >
        <div className="flex justify-between">
          <p className="mb-2 text-2xl font-bold leading-8">{translations.RelatedProducts.title}</p>
          <div className="top-1 flex flex-row gap-2 pr-8 mobile:hidden">
            <button
              aria-label="show previous items"
              disabled={refItems.firstItemInView}
              data-testid="previous-button"
              type="button"
              onClick={() => handleScroll('back')}
              className={clsx(
                'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
              )}
            >
              <BrgIconArrowBack />
            </button>
            <button
              aria-label="show next items"
              type="button"
              data-testid="next-button"
              disabled={refItems.lastItemInView}
              onClick={() => handleScroll('forward')}
              className={clsx(
                'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_BG} listpage_button_arrow_bg`,
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_FILL} listpage_button_arrow_icon_fill`,
                `${stylesBydomain.LISTPAGE_BUTTON_ARROW_ICON_STROKE} listpage_button_arrow_icon_stroke`,
              )}
            >
              <BrgIconArrowForward />
            </button>
          </div>
        </div>
        {discount > 0 && (
          <p className="mb-4 leading-5 text-neutral-600">{translations.RelatedProducts.subTitle(discount)}</p>
        )}

        <div
          className="no-scrollbar relative flex overflow-scroll"
          data-testid="related-products-container"
          ref={carouselRef}
        >
          {relatedProducts.map((product, index) => {
            return (
              <InViewComponent
                key={`${product.asin}-${index}`}
                setRefItems={setRefItems}
                itemIndex={index === 0 ? 0 : index === relatedProducts.length - 1 ? 1 : undefined}
              >
                <DwsRelatedProductCard
                  product={product}
                  index={index}
                  key={`${product.asin}-${index}`}
                  ref={index === 0 ? firstCardElementRef : null}
                />
              </InViewComponent>
            )
          })}
        </div>
      </section>
    )
  )
}

function InViewComponent({
  itemIndex,
  setRefItems,
  children,
}: {
  itemIndex: number | undefined
  setRefItems: Dispatch<SetStateAction<InViewRefCardProps>>
  children: ReactElement
}) {
  const {ref} = useInView({
    threshold: 0.9,
    onChange: (inView) => {
      if (itemIndex === 0) {
        setRefItems((prev) => ({...prev, firstItemInView: inView}))
      }
      if (itemIndex === 1) setRefItems((prev) => ({...prev, lastItemInView: inView}))
    },
  })

  return <div ref={ref}>{children}</div>
}
